import Vue from 'vue'
import userApi from '@/api/user.api'

const state = {
  currentUserId: null,
  currentUser: {},
  currentUserAndProjectClaims: null,
  users: {},
  usersShort: {},
  managers: {}
}


const getters = {
  currentUserId: (state) => {
    return state.currentUserId
  },

  currentUserAndProjectClaims: (state) => {
    return state.currentUserAndProjectClaims
  },

  currentUser: (state) => {
    return state.currentUser
  },

  userById: (state) => (id) => {
    return state.users[id]
  },

  userShortById: (state) => (id) => {
    return state.usersShort[id]
  },

  managerById: (state) => (id) => {
    return state.managers[id]
  },

  users: (state) => {
    return Object.values(state.users)
  },

  usersShort: (state) => {
    return Object.values(state.usersShort)
  },

  managers: (state) => {
    return Object.values(state.managers)
  },

  userIds: (state) => {
    return Object.keys(state.users)
  },

  userIdsShort: (state) => {
    return Object.keys(state.usersShort)
  },

  managerIds: (state) => {
    return Object.keys(state.managers)
  }
}


const actions = {
  async loadCurrentUser ({ commit, rootState }) {
    try {
      const response = await userApi.getCurrentUser(rootState.tenant.current)
      const user = response.data
      commit('SET_CURRENT_USER', user)
      localStorage.setItem('currentUser', JSON.stringify(user))
      return user
    } catch (error) {
      commit('SET_CURRENT_USER', null)
      localStorage.removeItem('currentUser')
      throw error
    }
  },

  async loadCurrentUserAndProjectClaims ({ commit, state, rootState }, currentProjectId) {
    try {
      const response = await userApi.getCurrentUserAndProjectClaims(rootState.tenant.current, state.currentUserId, currentProjectId)
      const currentUserAndProjectClaims = response.data
      commit('SET_CURRENT_USER_AND_PROJECT_CLAIMS', currentUserAndProjectClaims)
      return currentUserAndProjectClaims
    } catch (error) {
      commit('SET_CURRENT_USER_AND_PROJECT_CLAIMS', null)
      throw error
    }
  },

  restoreCurrentUser ({ commit }) {
    const json = localStorage.getItem('currentUser')

    if (json) {
      const user = JSON.parse(json)
      commit('SET_CURRENT_USER', user)
      return user
    }

    return null
  },

  async loadUsers ({ commit, rootState }) {
    const response = await userApi.getUsers(rootState.tenant.current)
    commit('SET_USERS', response.data)
  },

  async loadUsersShort ({ commit, rootState }) {
    const response = await userApi.getUsersShort(rootState.tenant.current)
    commit('SET_USERS_SHORT', response.data)
  },

  async loadUserById ({ commit, rootState }, userId) {
    const response = await userApi.getUser(rootState.tenant.current, userId)
    return response.data
  },

  async loadAndUpdateUser ({ commit, rootState }, entityData) {
    const response = await userApi.getUser(rootState.tenant.current, entityData.entityId)
    commit('UPDATE_USER', response.data)
    return response.data
  },

  async loadManagers ({ commit, rootState }) {
    const response = await userApi.getManagers(rootState.tenant.current)
    commit('SET_MANAGERS', response.data)
  },

  async createUser ({ commit, rootState }, user) {
    const response = await userApi.createUser(rootState.tenant.current, user)
    commit('CREATE_USER', response.data)
  },

  async setUserIsActive ({ commit, rootState }, { userId, isActive }) {
    const response = await userApi.setUserIsActive(rootState.tenant.current, userId, isActive)
    commit('UPDATE_USER', response.data)
  },

  async setUserRole ({ commit, rootState }, { userId, roleId }) {
    const response = await userApi.setUserRole(rootState.tenant.current, userId, roleId)
    commit('UPDATE_USER', response.data)
  },

  async updateUser ({ commit, rootState }, user) {
    const response = await userApi.updateUser(rootState.tenant.current, user.id, user)
    commit('UPDATE_USER', response.data)
  },

  async deleteUser ({ commit, rootState }, { id }) {
    await userApi.deleteUser(rootState.tenant.current, id)
    commit('DELETE_USER', id)
  },

  async inviteUser ({ commit, rootState }, data) {
    await userApi.inviteUser(rootState.tenant.current, data.contactId, data)
  },

  async loadRelations ({ commit, rootState }, id) {
    const response = await userApi.getRelations(rootState.tenant.current, id)
    return response.data
  }
}


const mutations = {
  SET_CURRENT_USER (state, user) {
    state.currentUserId = user ? user.id : null
    state.currentUser = { ...user }
    if (user) {
      Vue.set(state.users, user.id, user)
    }
  },

  SET_CURRENT_USER_AND_PROJECT_CLAIMS (state, currentUserAndProjectClaims) {
    state.currentUserAndProjectClaims = { ...currentUserAndProjectClaims }
  },

  SET_USERS (state, users) {
    state.users = {}
    for (const user of users) {
      Vue.set(state.users, user.id, user)
    }
  },

  SET_USERS_SHORT (state, usersShort) {
    state.usersShort = {}
    for (const userShort of usersShort) {
      Vue.set(state.usersShort, userShort.id, userShort)
    }
  },

  SET_MANAGERS (state, managers) {
    state.managers = {}
    for (const manager of managers) {
      Vue.set(state.managers, manager.id, manager)
    }
  },

  CREATE_USER (state, user) {
    Vue.set(state.users, user.id, user)
  },

  UPDATE_USER (state, user) {
    Vue.set(state.users, user.id, user)
  },

  DELETE_USER (state, id) {
    Vue.delete(state.users, id)
  }
}


export default {
  state,
  getters,
  actions,
  mutations
}
